import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import EditPlanModal from './components/EditPlanModal';

const SuperAdminAllPlans = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, plan: null });
  const [editModal, setEditModal] = useState({ isOpen: false, plan: null });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [plans, setPlans] = useState([
    {
      id: 1,
      name: 'Free',
      monthlyPrice: 0,
      yearlyPrice: 0,
      activeUsers: 342,
      featuresCount: 3,
      status: 'active'
    },
    {
      id: 2,
      name: 'Growth',
      monthlyPrice: 2999,
      yearlyPrice: 29990,
      activeUsers: 487,
      featuresCount: 7,
      status: 'active'
    },
    {
      id: 3,
      name: 'Scale',
      monthlyPrice: 5999,
      yearlyPrice: 59990,
      activeUsers: 218,
      featuresCount: 10,
      status: 'active'
    },
    {
      id: 4,
      name: 'Enterprise',
      monthlyPrice: 14999,
      yearlyPrice: 149990,
      activeUsers: 89,
      featuresCount: 15,
      status: 'active'
    },
    {
      id: 5,
      name: 'Starter',
      monthlyPrice: 999,
      yearlyPrice: 9990,
      activeUsers: 156,
      featuresCount: 5,
      status: 'inactive'
    }
  ]);

  const handleToggleStatus = (planId) => {
    setPlans(plans?.map(p => 
      p?.id === planId ? { ...p, status: p?.status === 'active' ? 'inactive' : 'active' } : p
    ));
  };

  const handleDelete = (plan) => {
    setDeleteModal({ isOpen: true, plan });
  };

  const confirmDelete = () => {
    setPlans(plans?.filter(p => p?.id !== deleteModal?.plan?.id));
    setDeleteModal({ isOpen: false, plan: null });
  };

  const handleEdit = (plan) => {
    setEditModal({ isOpen: true, plan });
  };

  const handleSaveEdit = (updatedPlan) => {
    setPlans(plans?.map(p => p?.id === updatedPlan?.id ? updatedPlan : p));
    setEditModal({ isOpen: false, plan: null });
  };

  const filteredPlans = plans?.filter(plan => {
    const matchesSearch = plan?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || plan?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredPlans?.length / itemsPerPage);
  const paginatedPlans = filteredPlans?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading flex items-center gap-3">
              <Icon name="Package" size={32} className="text-primary" />
              All Subscription Plans
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body mt-2">
              Manage all subscription plans, pricing, and features
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e?.target?.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search plans by name..."
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e?.target?.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => window.location.href = '/super-admin-create-plan'}
              >
                Create Plan
              </Button>
            </div>
          </div>

          {/* Plans Table - Desktop */}
          <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Plan Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Monthly Price</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Yearly Price</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Active Users</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Features</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPlans?.map((plan) => (
                  <tr key={plan?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                    <td className="px-4 py-4">
                      <span className="font-semibold text-foreground">{plan?.name}</span>
                    </td>
                    <td className="px-4 py-4 text-foreground">₹{plan?.monthlyPrice?.toLocaleString()}</td>
                    <td className="px-4 py-4 text-foreground">₹{plan?.yearlyPrice?.toLocaleString()}</td>
                    <td className="px-4 py-4 text-foreground">{plan?.activeUsers}</td>
                    <td className="px-4 py-4 text-foreground">{plan?.featuresCount}</td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleToggleStatus(plan?.id)}
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-smooth ${
                          plan?.status === 'active' ? 'bg-success/10 text-success hover:bg-success/20' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full ${
                          plan?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                        }`} />
                        {plan?.status === 'active' ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(plan)}
                          className="p-2 hover:bg-muted rounded-md transition-smooth"
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} className="text-primary" />
                        </button>
                        <button
                          onClick={() => handleDelete(plan)}
                          className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={16} className="text-destructive" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Plans Cards - Mobile */}
          <div className="lg:hidden space-y-4 mb-6">
            {paginatedPlans?.map((plan) => (
              <div key={plan?.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg">{plan?.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{plan?.featuresCount} features</p>
                  </div>
                  <button
                    onClick={() => handleToggleStatus(plan?.id)}
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                      plan?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${
                      plan?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                    }`} />
                    {plan?.status === 'active' ? 'Active' : 'Inactive'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Monthly</p>
                    <p className="text-sm font-semibold text-foreground">₹{plan?.monthlyPrice?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Yearly</p>
                    <p className="text-sm font-semibold text-foreground">₹{plan?.yearlyPrice?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Active Users</p>
                    <p className="text-sm font-semibold text-foreground">{plan?.activeUsers}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(plan)}
                    iconName="Edit"
                    iconPosition="left"
                    fullWidth
                  >
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(plan)}
                    iconName="Trash2"
                    iconPosition="left"
                    fullWidth
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredPlans?.length)} of {filteredPlans?.length} plans
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  iconName="ChevronLeft"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)?.map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-smooth ${
                        currentPage === page
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* Modals */}
          <DeleteConfirmModal
            isOpen={deleteModal?.isOpen}
            plan={deleteModal?.plan}
            onClose={() => setDeleteModal({ isOpen: false, plan: null })}
            onConfirm={confirmDelete}
          />
          <EditPlanModal
            isOpen={editModal?.isOpen}
            plan={editModal?.plan}
            onClose={() => setEditModal({ isOpen: false, plan: null })}
            onSave={handleSaveEdit}
          />
        </div>
      </div>
    </div>
  );
};

export default SuperAdminAllPlans;